import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import * as React from 'react';

import styles from './input.module.scss';

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  classes?: {
    container?: string;
    input?: string;
    leftAdornment?: string;
    rightAdornment?: string;
  };
  variant?: 'contained' | 'outlined';

  hasError?: boolean;

  leftAdornment?: React.ReactNode;
  rightAdornment?: React.ReactNode;
};

const inputContainerVariants = cva(styles.container, {
  variants: {
    variant: {
      contained: styles.containerContained,
      outlined: styles.containerOutlined,
    },
  },
});

const inputVariants = cva(styles.input, {
  variants: {
    variant: {
      contained: styles.inputContained,
      outlined: styles.inputOutlined,
    },
  },
});

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      variant,
      classes,
      leftAdornment,
      rightAdornment,
      hasError,
      disabled = false,
      ...props
    },
    ref
  ) => (
    <div
      className={clsx(
        inputContainerVariants({ variant }),
        classes?.container,
        styles.containerSize,
        {
          [styles.containerHasError]: hasError,
          [styles.containerContainedDisabled]:
            variant === 'contained' && disabled,
          [styles.containerOutlinedDisabled]:
            variant === 'outlined' && disabled,
          [styles.containerSizeHasLeftAdornment]: !!leftAdornment,
          [styles.containerSizeHasRightAdornment]: !!rightAdornment,
        }
      )}
    >
      {leftAdornment && (
        <div className={clsx(styles.leftAdornment, classes?.leftAdornment)}>
          {leftAdornment}
        </div>
      )}

      <input
        ref={ref}
        type={type}
        className={clsx(inputVariants({ variant }), className, classes?.input)}
        disabled={disabled}
        {...props}
      />

      {rightAdornment && (
        <div className={clsx(styles.rightAdornment, classes?.rightAdornment)}>
          {rightAdornment}
        </div>
      )}
    </div>
  )
);
Input.displayName = 'Input';

export { Input };
export type { InputProps };
